import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaSyncAlt, FaPaperPlane } from 'react-icons/fa';
import { useParams, useNavigate, generatePath, useMatch } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  AgreementStatus,
  PermissionKeys,
  useGetAgreementsByIdQuery,
  useLazyGetAgreementsByIdQuery,
  usePutAgreementsByIdMutation,
  usePostAgreementsByIdRestoreMutation,
  usePostAgreementsByIdSyncTripletexMutation,
} from '@client/api';
import { Button, Modal, ModalContent, ModalFooter, Typography, Spinner } from '@client/toolkit';
import { useAgreementPermissions } from '../../../../hooks/useAgreementPermissions/useAgreementPermissions';
import { AgreementsPaths } from '../../agreements.router';
import { useAppSelector } from '../../../../hooks';
import { selectSelectedTenant } from '../../../../redux/session/session.slice';

const InvoicePreviewButton = ({ agreementId }: { agreementId: string }) => {
  const { t } = useTranslation('agreements');
  const navigate = useNavigate();
  const [syncTripletex, { isLoading: isSyncing }] = usePostAgreementsByIdSyncTripletexMutation();
  const [fetchAgreement] = useLazyGetAgreementsByIdQuery();

  const handleClick = async () => {
    await syncTripletex({ id: agreementId });
    await fetchAgreement({ id: agreementId });
    navigate(generatePath(AgreementsPaths.AgreementInvoicing, { id: agreementId }));
  };

  return (
    <Button color="custom" size="sm" className="border border-border bg-transparent hover:bg-white/5 text-white font-normal" onClick={handleClick} disabled={isSyncing}>
      {isSyncing ? <Spinner className="mr-2" /> : <FaSyncAlt className="mr-2" />}
      {t('wizard.tooltip.invoicePreview')}
    </Button>
  );
};

const ApproveAndSendChangesButton = ({ agreementId }: { agreementId: string }) => {
  const { t } = useTranslation('agreements');
  const [syncTripletex, { isLoading: isSyncing }] = usePostAgreementsByIdSyncTripletexMutation();
  const [fetchAgreement] = useLazyGetAgreementsByIdQuery();

  const handleClick = async () => {
    try {
      await syncTripletex({ id: agreementId }).unwrap();
      await fetchAgreement({ id: agreementId });
      toast.success(t('wizard.actions.changesSubmitted'), { id: 'changes-submitted' });
    } catch {
      toast.error(t('wizard.actions.changesSubmitFailed'), { id: 'changes-submit-failed' });
    }
  };

  return (
    <Button
      color="custom"
      size="sm"
      className="border border-green-600 bg-green-950/40 hover:bg-green-950/60 text-white font-normal"
      onClick={handleClick}
      disabled={isSyncing}
    >
      {isSyncing ? <Spinner className="mr-2" /> : <FaPaperPlane className="mr-2" />}
      {t('wizard.actions.godkjennOgSendEndringer')}
    </Button>
  );
};

export function AgreementWizardActions() {
  const { t } = useTranslation('agreements');
  const { t: tCommon } = useTranslation('common');
  const { t: tType } = useTranslation('type');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isOnInvoicing = useMatch(AgreementsPaths.AgreementInvoicing) != null;

  // Check if ERP is configured for the selected tenant from session
  const selectedTenant = useAppSelector(selectSelectedTenant);
  const isErpConfigured = selectedTenant && selectedTenant !== 'all'
    ? !!selectedTenant.erpIntegrations?.some((erp) => erp.configured)
    : false;

  const { data: agreement, isFetching, isLoading } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });
  const { hasPermission } = useAgreementPermissions({ id: id ?? '' });
  const [putAgreement, { isLoading: isUpdating, error }] = usePutAgreementsByIdMutation();
  const [restoreAgreement, { isLoading: isRestoring }] = usePostAgreementsByIdRestoreMutation();

  const [selectedStatus, setSelectedStatus] = useState<AgreementStatus | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [restoreConfirming, setRestoreConfirming] = useState(false);

  const currentStatus = agreement?.status;

  useEffect(() => {
    if (error && 'status' in error && error.status === 400) {
      const errorData = 'data' in error ? (error.data as { errors?: Record<string, string[]>; detail?: string }) : null;
      let validationErrors: string[] = [];
      if (errorData?.errors) {
        validationErrors = Object.values(errorData.errors).flat();
      }
      if (validationErrors.length > 0) {
        const errorMessages = validationErrors.map((code: string) =>
          t(`agreement.validationErrors.${code}`, { defaultValue: code }),
        );
        toast.error(
          <div className="max-w-md">
            <div className="font-semibold mb-2">{t('agreement.statusChangeError')}</div>
            <ul className="text-sm list-disc pl-4 space-y-1">
              {errorMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>,
          { id: 'global-error', duration: 8000 },
        );
      } else if (errorData?.detail && errorData.detail !== 'Bad request') {
        toast.error(
          <div className="max-w-md">
            <div className="font-semibold mb-2">{t('agreement.statusChangeError')}</div>
            <div className="text-sm">{errorData.detail}</div>
          </div>,
          { id: 'global-error', duration: 8000 },
        );
      } else {
        toast.error(t('agreement.statusChangeError'), { id: 'global-error' });
      }
    }
  }, [error, t]);

  const requestTransition = (status: AgreementStatus) => {
    setSelectedStatus(status);
    setConfirming(true);
  };

  const handleConfirm = async () => {
    if (id == null || agreement == null || selectedStatus == null) return;
    try {
      await putAgreement({ id, body: { ...agreement, status: selectedStatus } }).unwrap();
      setConfirming(false);
      if (selectedStatus === 'Ready' && currentStatus === 'Draft' && !isOnInvoicing) {
        navigate(generatePath(AgreementsPaths.AgreementInvoicing, { id }));
      } else if (selectedStatus === 'Draft' || selectedStatus === 'Effective') {
        navigate(generatePath(AgreementsPaths.Agreement, { id }));
      }
    } catch {
      setConfirming(false);
    }
  };

  const handleCloseConfirm = () => {
    setConfirming(false);
  };

  const handleOpenRestore = () => setRestoreConfirming(true);
  const handleCloseRestore = () => {
    if (isRestoring) return;
    setRestoreConfirming(false);
  };

  const handleConfirmRestore = async () => {
    if (id == null) return;
    try {
      const result = await restoreAgreement({ id }).unwrap();
      toast.success(t('agreement.restore.success'));
      setRestoreConfirming(false);
      navigate(generatePath(AgreementsPaths.Agreement, { id: result.id }));
    } catch {
      toast.error(t('agreement.restore.error'));
    }
  };

  const canEdit = hasPermission([PermissionKeys.Agreement.Update]) && !isFetching && !isLoading;
  const canRestore = hasPermission([PermissionKeys.Agreement.Create]) && !isFetching && !isLoading;

  return (
    <>
      <div className="flex items-center gap-2">
        {currentStatus === 'Draft' && (
          <Button color="primary" size="sm" disabled={!canEdit} onClick={() => requestTransition('Ready')}>
            {t('wizard.actions.markerSomKlar')}
          </Button>
        )}

        {currentStatus === 'Ready' && (
          <>
            {isErpConfigured && <InvoicePreviewButton agreementId={id ?? ''} />}
            <Button
              color="custom"
              size="sm"
              className="border border-green-600 bg-green-950/40 hover:bg-green-950/60 text-white font-normal"
              disabled={!canEdit}
              onClick={() => requestTransition('Effective')}
            >
              {t('wizard.actions.aktiverAvtale')}
            </Button>
          </>
        )}

        {currentStatus === 'Effective' && (
          <>
            {isErpConfigured && <InvoicePreviewButton agreementId={id ?? ''} />}
            {isErpConfigured && agreement?.hasTripletexPendingSync && (
              <ApproveAndSendChangesButton agreementId={id ?? ''} />
            )}
            <Button color="custom" size="sm" className="border border-red-600 bg-red-950/40 hover:bg-red-950/60 text-white font-normal" disabled={!canEdit} onClick={() => requestTransition('Terminated')}>
              {t('wizard.actions.avsluttAvtale')}
            </Button>
          </>
        )}

        {currentStatus === 'Terminated' && (
          <Button color="primary-dark" size="sm" disabled={!canRestore || isRestoring} onClick={handleOpenRestore}>
            {t('wizard.actions.gjenaktiverAvtale')}
          </Button>
        )}
      </div>

      <Modal
        size="sm"
        open={confirming}
        onClose={handleCloseConfirm}
        onBackdropClick={handleCloseConfirm}
        closeDisabled={isUpdating}
      >
        <ModalContent>
          <div className="uppercase text-xs font-semibold p-4 text-center">{t('agreement.changeStatus')}</div>
          <Typography align="center" className="px-4 py-4">
            <Trans
              t={t}
              i18nKey="agreement.confirmChangeStatus"
              values={{
                fromStatus: tType(`AgreementStatus.${currentStatus}`),
                toStatus: tType(`AgreementStatus.${selectedStatus}`),
              }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </ModalContent>
        <ModalFooter className="justify-center">
          <Button color="primary" onClick={handleConfirm} progressing={isUpdating}>
            {tCommon('confirm')}
          </Button>
          <Button color="primary-dark" onClick={handleCloseConfirm} disabled={isUpdating}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        size="sm"
        open={restoreConfirming}
        onClose={handleCloseRestore}
        onBackdropClick={handleCloseRestore}
        closeDisabled={isRestoring}
      >
        <ModalContent>
          <div className="uppercase text-xs font-semibold p-4 text-center">
            {t('agreement.restore.confirmTitle')}
          </div>
          <Typography align="center" className="px-4 py-4">
            <Trans
              t={t}
              i18nKey="agreement.restore.confirmBody"
              values={{ name: `${agreement?.name ?? ''} copy` }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </ModalContent>
        <ModalFooter className="justify-center">
          <Button color="primary" onClick={handleConfirmRestore} progressing={isRestoring}>
            {tCommon('confirm')}
          </Button>
          <Button color="primary-dark" onClick={handleCloseRestore} disabled={isRestoring}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
